import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Drawer,
  IconButton,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../utils/api';

function contactLabel(c) {
  if (!c) return '';
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return `${name} — ${c.email || ''}`;
}

function empName(row) {
  const e = row.employee;
  if (!e) return '—';
  return `${e.first_name || ''} ${e.last_name || ''}`.trim() || '—';
}

export default function EverifyExistingList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);

  const [openAdd, setOpenAdd] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [hireDateNew, setHireDateNew] = useState('');
  const [jobTitleNew, setJobTitleNew] = useState('');
  const [notesNew, setNotesNew] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [everifyRef, setEverifyRef] = useState('');
  const [waExpiry, setWaExpiry] = useState('');
  const [citizenshipNote, setCitizenshipNote] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('open');
  const [parseHint, setParseHint] = useState(null);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/immigration/everify/existing-cases', { params: { page, limit: 25 } });
      setRows(res.data?.data || []);
      const p = res.data?.pagination;
      if (p) setPagination({ page: p.page, totalPages: p.totalPages, total: p.total });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load cases.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const loadEmployees = useCallback(async () => {
    setContactLoading(true);
    try {
      const res = await api.get('/contacts', {
        params: { contact_type: 'employee', status: 'active', limit: 500, page: 1 },
      });
      setContactOptions(res.data?.data || []);
    } catch {
      setContactOptions([]);
    } finally {
      setContactLoading(false);
    }
  }, []);

  const openAddDialog = () => {
    setFormError('');
    setSelectedEmployee(null);
    setHireDateNew('');
    setJobTitleNew('');
    setNotesNew('');
    setOpenAdd(true);
    loadEmployees();
  };

  const createCase = async () => {
    setFormError('');
    if (!selectedEmployee) {
      setFormError('Select an employee.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/immigration/everify/existing-cases', {
        employee_contact_id: selectedEmployee.id,
        hire_date: hireDateNew || undefined,
        job_title: jobTitleNew.trim() || undefined,
        notes: notesNew.trim() || undefined,
      });
      setOpenAdd(false);
      loadCases();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to create case.');
    } finally {
      setSaving(false);
    }
  };

  const loadDetail = async (id) => {
    setDetailLoading(true);
    setDetailError('');
    setParseHint(null);
    try {
      const res = await api.get(`/immigration/everify/existing-cases/${id}`);
      const d = res.data?.data;
      setDetail(d);
      setHireDate(d?.hire_date || '');
      setJobTitle(d?.job_title || '');
      setEverifyRef(d?.everify_case_reference || '');
      setWaExpiry(d?.work_authorization_expiry || '');
      setCitizenshipNote(d?.citizenship_status_note || '');
      setNotes(d?.notes || '');
      setStatus(d?.status || 'open');
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Failed to load case.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDrawer = (id) => {
    setActiveId(id);
    setDrawerOpen(true);
    loadDetail(id);
  };

  const saveFields = async () => {
    if (!activeId) return;
    setDetailError('');
    try {
      await api.patch(`/immigration/everify/existing-cases/${activeId}`, {
        hire_date: hireDate || null,
        job_title: jobTitle.trim() || null,
        everify_case_reference: everifyRef.trim() || null,
        work_authorization_expiry: waExpiry || null,
        citizenship_status_note: citizenshipNote.trim() || null,
        notes: notes.trim() || null,
        status,
      });
      await loadDetail(activeId);
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Save failed.');
    }
  };

  const uploadFile = async (file, kind) => {
    if (!activeId || !file) return;
    setDetailError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    try {
      const res = await api.post(`/immigration/everify/existing-cases/${activeId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = res.data?.data;
      const hints = payload?.parse_hints;
      if (hints?.hire_date) setParseHint(hints);
      else setParseHint(null);
      const d = payload?.case || payload;
      setDetail(d);
      if (d) {
        setHireDate(d?.hire_date || '');
        setJobTitle(d?.job_title || '');
        setEverifyRef(d?.everify_case_reference || '');
        setWaExpiry(d?.work_authorization_expiry || '');
        setCitizenshipNote(d?.citizenship_status_note || '');
        setNotes(d?.notes || '');
        setStatus(d?.status || 'open');
      }
      loadCases();
    } catch (e) {
      setDetailError(e.response?.data?.message || 'Upload failed.');
    }
  };

  const downloadDoc = async (documentId, fileName) => {
    if (!activeId || !documentId) return;
    try {
      const res = await api.get(
        `/immigration/everify/existing-cases/${activeId}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDetailError('Download failed.');
    }
  };

  const applyHireHint = () => {
    if (parseHint?.hire_date) {
      setHireDate(parseHint.hire_date);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        E-Verify — Existing employee
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track employees who already have Form I-9 and E-Verify handled elsewhere: store the I-9 and USCIS/E-Verify
        result PDFs, hire date, and references. Text-based I-9 PDFs may suggest a hire date after upload; always
        verify on the form.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
              New case
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Hire date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell align="right">Open</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No cases yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{empName(row)}</TableCell>
                        <TableCell>{row.employee?.email || '—'}</TableCell>
                        <TableCell>{row.hire_date || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status === 'closed' ? 'Closed' : 'Open'} color={row.status === 'closed' ? 'default' : 'primary'} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {row.i9Document && <Chip size="small" label="I-9" variant="outlined" />}
                            {row.everifyResultDocument && <Chip size="small" label="E-Verify PDF" variant="outlined" />}
                            {!row.i9Document && !row.everifyResultDocument && '—'}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={() => openDrawer(row.id)}>
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.totalPages > 1 && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {pagination.totalPages} ({pagination.total} total)
              </Typography>
              <Button
                size="small"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={openAdd} onClose={() => !saving && setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New E-Verify existing case</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={contactOptions}
              loading={contactLoading}
              getOptionLabel={(o) => contactLabel(o)}
              value={selectedEmployee}
              onChange={(_, v) => setSelectedEmployee(v)}
              renderInput={(params) => (
                <TextField {...params} label="Employee" size="small" helperText="Employee contacts only." />
              )}
            />
            <TextField label="Hire date (optional)" type="date" size="small" InputLabelProps={{ shrink: true }} value={hireDateNew} onChange={(e) => setHireDateNew(e.target.value)} fullWidth />
            <TextField label="Job title (optional)" size="small" value={jobTitleNew} onChange={(e) => setJobTitleNew(e.target.value)} fullWidth />
            <TextField label="Notes (optional)" size="small" multiline minRows={2} value={notesNew} onChange={(e) => setNotesNew(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createCase} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">E-Verify existing</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2, overflow: 'auto' }}>
          {detailLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          {!detailLoading && detail && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {empName({ employee: detail.employee })}
              </Typography>
              <Typography variant="body2">{detail.employee?.email}</Typography>

              {parseHint?.hire_date && (
                <Alert severity="info" action={<Button onClick={applyHireHint}>Apply hire date</Button>}>
                  Suggested hire date from PDF text: {parseHint.hire_date}. {parseHint.note || ''}
                </Alert>
              )}

              <TextField label="Hire date" type="date" size="small" InputLabelProps={{ shrink: true }} value={hireDate} onChange={(e) => setHireDate(e.target.value)} fullWidth />
              <TextField label="Job title" size="small" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} fullWidth />
              <TextField label="E-Verify case reference" size="small" value={everifyRef} onChange={(e) => setEverifyRef(e.target.value)} fullWidth />
              <TextField label="Work authorization expiry" type="date" size="small" InputLabelProps={{ shrink: true }} value={waExpiry} onChange={(e) => setWaExpiry(e.target.value)} fullWidth />
              <TextField label="Citizenship / status note" size="small" value={citizenshipNote} onChange={(e) => setCitizenshipNote(e.target.value)} fullWidth multiline minRows={2} />
              <TextField label="Notes" size="small" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
              <TextField label="Status" select size="small" value={status} onChange={(e) => setStatus(e.target.value)} fullWidth SelectProps={{ native: true }}>
                <option value="open">open</option>
                <option value="closed">closed</option>
              </TextField>

              <Button variant="contained" onClick={saveFields}>
                Save fields
              </Button>

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">I-9 (PDF or scan)</Typography>
              <Button variant="outlined" component="label">
                Upload / replace I-9
                <input type="file" hidden accept=".pdf,image/*" onChange={(e) => uploadFile(e.target.files?.[0], 'i9')} />
              </Button>
              {detail.i9Document && (
                <Button
                  startIcon={<DownloadIcon />}
                  size="small"
                  onClick={() => downloadDoc(detail.i9Document.id, detail.i9Document.file_name)}
                >
                  {detail.i9Document.file_name}
                </Button>
              )}

              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                E-Verify / USCIS result (PDF)
              </Typography>
              <Button variant="outlined" component="label">
                Upload / replace result PDF
                <input type="file" hidden accept="application/pdf,.pdf" onChange={(e) => uploadFile(e.target.files?.[0], 'everify_result')} />
              </Button>
              {detail.everifyResultDocument && (
                <Button
                  startIcon={<DownloadIcon />}
                  size="small"
                  onClick={() => downloadDoc(detail.everifyResultDocument.id, detail.everifyResultDocument.file_name)}
                >
                  {detail.everifyResultDocument.file_name}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
